import { useState, useEffect } from 'react'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { FileText, Plus, CheckCircle2 } from 'lucide-react'
import { countTokenPatterns } from '@/utils/tokenPatterns'

interface TemplateInfo {
  id: string
  name: string | null
  tool_name: string | null
  version: number
  status: string
  extraction_config?: Record<string, unknown>
}

type SelectionResult = { type: 'selected'; templateId: string } | { type: 'skipped' } | { type: 'cancelled' }

export function TemplateSelectionDialog({ isOpen, templates, onResult }: { isOpen: boolean; templates: TemplateInfo[]; onResult: (result: SelectionResult) => void }) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)

  const activeTemplates = templates.filter(t => t.status === 'active' || t.status === 'pending_review')

  useEffect(() => {
    if (isOpen) {
      const sorted = [...activeTemplates].sort((a, b) => {
        const aConfig = a.extraction_config as Record<string, unknown> | undefined
        const bConfig = b.extraction_config as Record<string, unknown> | undefined
        const aUpdated = (aConfig?.last_updated_at as string) || ''
        const bUpdated = (bConfig?.last_updated_at as string) || ''
        const timeDiff = bUpdated.localeCompare(aUpdated)
        if (timeDiff !== 0) return timeDiff
        const versionDiff = b.version - a.version
        if (versionDiff !== 0) return versionDiff
        return b.id.localeCompare(a.id)
      })
      if (sorted.length > 0) {
        setMode('existing')
        setSelectedTemplate(sorted[0]!.id)
      } else {
        setMode('new')
        setSelectedTemplate(null)
      }
    }
  }, [isOpen, activeTemplates])

  const getTokenPatternCount = (template: TemplateInfo): number => {
    const config = template.extraction_config as { token_patterns?: Record<string, unknown> } | undefined
    return countTokenPatterns(config?.token_patterns)
  }

  const handleConfirm = () => {
    if (mode === 'new') {
      onResult({ type: 'skipped' })
    } else if (selectedTemplate) {
      onResult({ type: 'selected', templateId: selectedTemplate })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => onResult({ type: 'cancelled' })} title="Template auswählen" size="lg">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setMode('existing')}
            disabled={activeTemplates.length === 0}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
              mode === 'existing'
                ? 'border-blue-500 bg-blue-50'
                : activeTemplates.length === 0
                  ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              mode === 'existing' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
            }`}>
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium text-sm">Bestehendes Template</div>
              <div className="text-xs text-gray-500">
                {activeTemplates.length > 0
                  ? `${activeTemplates.length} verfügbar`
                  : 'Keine verfügbar'}
              </div>
            </div>
            {mode === 'existing' && <CheckCircle2 className="w-5 h-5 text-blue-500 ml-auto" />}
          </button>

          <button
            onClick={() => setMode('new')}
            className={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
              mode === 'new'
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              mode === 'new' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
            }`}>
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium text-sm">Neues Template</div>
              <div className="text-xs text-gray-500">Aus Tokens erstellt</div>
            </div>
            {mode === 'new' && <CheckCircle2 className="w-5 h-5 text-green-500 ml-auto" />}
          </button>
        </div>

        {mode === 'existing' && activeTemplates.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
            {activeTemplates.map(template => {
              const patternCount = getTokenPatternCount(template)
              const isSelected = selectedTemplate === template.id
              return (
                <button
                  key={template.id}
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedTemplate(template.id)}
                >
                  <div className="font-medium text-sm">{template.name || `Template ${template.id.slice(0, 8)}`}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {template.tool_name || 'Token-Pipeline'} · v{template.version}
                    {patternCount > 0 && ` · ${patternCount} Token-Pattern`}
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {mode === 'new' && (
          <div className="p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-sm text-green-800">
              <strong>Neues Template:</strong> Die Extraktion erfolgt ohne bestehendes Template.
              Nach der Token-Zuordnung wird automatisch ein neues Template aus deinen Zuordnungen erstellt.
            </p>
          </div>
        )}

        {mode === 'existing' && activeTemplates.length === 0 && (
          <div className="p-4 rounded-lg bg-yellow-50 border border-yellow-200">
            <p className="text-sm text-yellow-800">
              Keine Templates vorhanden. Wähle "Neues Template" um ein neues zu erstellen.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="secondary" size="sm" onClick={() => onResult({ type: 'cancelled' })}>
            Abbrechen
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={(mode === 'existing' && !selectedTemplate) || (mode === 'existing' && activeTemplates.length === 0)}
          >
            {mode === 'new' ? 'Ohne Template extrahieren' : 'Mit Template extrahieren'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
